import { useState, useEffect, useRef } from "react";
import { ROUTES } from "@/constants/routes";

interface NavItem {
  route: string;
  label: string;
}

// All navigation items in priority order (most important first)
// Items are shown in main nav as width allows, overflow goes to "More"
const ALL_NAV_ITEMS: NavItem[] = [
  { route: ROUTES.HOME, label: "Home" },
  { route: ROUTES.ABOUT_US, label: "About Us" },
  { route: ROUTES.ECOSYSTEM, label: "Ecosystem" },
  { route: ROUTES.AI_ENGINE, label: "AI Engine" },
  { route: ROUTES.TOKENOMICS, label: "Tokenomics" },
  { route: ROUTES.ELEVATE_TO_ALPHA, label: "Elevate to Alpha" },
  { route: ROUTES.TRACTION, label: "Traction" },
  { route: ROUTES.ROADMAP, label: "Roadmap" },
  { route: ROUTES.USER_JOURNEYS, label: "User Journeys" },
  { route: ROUTES.OUR_TEAM, label: "Our Team" },
  { route: ROUTES.REGULATORY_COMPLIANCE, label: "Regulatory Compliance" },
  { route: ROUTES.SOCIAL_IMPACT_COLLECTIVE, label: "Social Impact Collective" },
  { route: ROUTES.IGAC, label: "IGAC" },
];

const MOBILE_BREAKPOINT_PX = 481;
const SAFETY_MARGIN_PX = 40;

/**
 * Estimate the width needed for a nav item based on its label
 * Accounts for font size, tracking, and gap
 * More accurate estimation based on system font (sans-serif)
 */
function estimateNavItemWidth(label: string, screenWidth: number): number {
  // Base font size: 16px on standard, 18px on 2xl, 20px on ultra-wide
  let avgCharWidth: number;
  let gap: number;

  if (screenWidth >= 2200) {
    avgCharWidth = 10.5;
    gap = 40;
  } else if (screenWidth >= 1536) {
    avgCharWidth = 9.5;
    gap = 32;
  } else {
    avgCharWidth = 8.5;
    gap = 24;
  }

  const textWidth = label.length * avgCharWidth;
  return textWidth + gap;
}

/**
 * Estimate the width needed for the "More" dropdown button
 */
function estimateMoreButtonWidth(screenWidth: number): number {
  let avgCharWidth: number;
  let itemGap: number;

  if (screenWidth >= 2200) {
    avgCharWidth = 10.5;
    itemGap = 40;
  } else if (screenWidth >= 1536) {
    avgCharWidth = 9.5;
    itemGap = 32;
  } else {
    avgCharWidth = 8.5;
    itemGap = 24;
  }

  const textWidth = 4 * avgCharWidth;
  const chevronWidth = 16;
  const gap = 4;
  return textWidth + chevronWidth + gap + itemGap;
}

function getNavItemGap(screenWidth: number): number {
  if (screenWidth >= 2200) return 40;
  if (screenWidth >= 1536) return 32;
  return 24;
}

function estimateLogoWidth(screenWidth: number): number {
  if (screenWidth >= 2200) return 400;
  if (screenWidth >= 1536) return 380;
  return 360;
}

function estimateCtaWidth(screenWidth: number): number {
  if (screenWidth >= 2200) return 240;
  if (screenWidth >= 1536) return 220;
  return 200;
}

function measureLogoWidth(logoLink: HTMLElement | null, screenWidth: number): number {
  if (!logoLink) return estimateLogoWidth(screenWidth);
  const logoStyles = globalThis.getComputedStyle(logoLink);
  const logoPaddingRight = Number.parseFloat(logoStyles.paddingRight) || 0;
  const logoMarginRight = Number.parseFloat(logoStyles.marginRight) || 0;
  return logoLink.offsetWidth + logoPaddingRight + logoMarginRight;
}

function measureCtaWidth(ctaButton: HTMLElement | null, screenWidth: number): number {
  if (!ctaButton) return estimateCtaWidth(screenWidth);
  const ctaStyles = globalThis.getComputedStyle(ctaButton);
  const ctaMarginLeft = Number.parseFloat(ctaStyles.marginLeft) || 0;
  return ctaButton.offsetWidth + ctaMarginLeft;
}

function measureNavPadding(nav: HTMLElement | null): number {
  if (!nav) return 0;
  const navStyles = globalThis.getComputedStyle(nav);
  return (
    (Number.parseFloat(navStyles.paddingLeft) || 0) +
    (Number.parseFloat(navStyles.paddingRight) || 0)
  );
}

function splitNavItems(
  availableSpace: number,
  screenWidth: number
): { itemsToShow: NavItem[]; itemsToHide: NavItem[] } {
  const moreButtonWidth = estimateMoreButtonWidth(screenWidth);
  const gap = getNavItemGap(screenWidth);

  for (let count = ALL_NAV_ITEMS.length; count >= 0; count--) {
    const testItems = ALL_NAV_ITEMS.slice(0, count);
    let totalWidth = 0;

    for (let i = 0; i < testItems.length; i++) {
      const itemWidth = estimateNavItemWidth(testItems[i].label, screenWidth);
      totalWidth += i === testItems.length - 1 ? itemWidth - gap : itemWidth;
    }

    if (count < ALL_NAV_ITEMS.length) {
      totalWidth += moreButtonWidth;
    }

    if (totalWidth <= availableSpace) {
      return {
        itemsToShow: testItems,
        itemsToHide: ALL_NAV_ITEMS.slice(count),
      };
    }
  }

  const firstItemWidth = estimateNavItemWidth(ALL_NAV_ITEMS[0].label, screenWidth);
  if (availableSpace > firstItemWidth + moreButtonWidth) {
    return { itemsToShow: [ALL_NAV_ITEMS[0]], itemsToHide: ALL_NAV_ITEMS.slice(1) };
  }

  return { itemsToShow: [], itemsToHide: ALL_NAV_ITEMS };
}

/**
 * Hook to determine which navigation items should be visible
 * based on actual available space to prevent wrapping
 */
export function useResponsiveNav() {
  const [visibleItems, setVisibleItems] = useState<NavItem[]>([]);
  const [moreItems, setMoreItems] = useState<NavItem[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const updateVisibleItems = () => {
      if (globalThis.innerWidth < MOBILE_BREAKPOINT_PX || !containerRef.current) {
        setVisibleItems([]);
        setMoreItems(ALL_NAV_ITEMS);
        return;
      }

      const container = containerRef.current;
      const containerWidth = container.offsetWidth;
      const innerContainer = container.querySelector(
        ".relative.flex.items-center"
      ) as HTMLElement | null;
      const logoLink = innerContainer?.querySelector(
        "a:first-of-type"
      ) as HTMLElement | null;
      const ctaButton = innerContainer?.querySelector(
        'a[aria-label="Whitepaper"]'
      ) as HTMLElement | null;
      const nav = container.querySelector(
        'nav[id="main-navigation"]'
      ) as HTMLElement | null;

      const screenWidth = globalThis.innerWidth;
      const containerStyles = globalThis.getComputedStyle(container);
      const containerPadding =
        (Number.parseFloat(containerStyles.paddingLeft) || 0) +
        (Number.parseFloat(containerStyles.paddingRight) || 0);

      const availableSpace =
        containerWidth -
        measureLogoWidth(logoLink, screenWidth) -
        measureCtaWidth(ctaButton, screenWidth) -
        containerPadding -
        measureNavPadding(nav) -
        SAFETY_MARGIN_PX;

      const { itemsToShow, itemsToHide } = splitNavItems(availableSpace, screenWidth);
      setVisibleItems(itemsToShow);
      setMoreItems(itemsToHide);
    };

    const timeoutId = setTimeout(updateVisibleItems, 100);

    let resizeTimeout: ReturnType<typeof setTimeout>;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(updateVisibleItems, 100);
    };

    globalThis.addEventListener("resize", handleResize);

    let resizeObserver: ResizeObserver | null = null;
    const container = containerRef.current;
    if (container) {
      resizeObserver = new ResizeObserver(updateVisibleItems);
      resizeObserver.observe(container);
    }

    return () => {
      clearTimeout(timeoutId);
      clearTimeout(resizeTimeout);
      globalThis.removeEventListener("resize", handleResize);
      if (resizeObserver && container) {
        resizeObserver.unobserve(container);
      }
    };
  }, []);

  return { visibleItems, moreItems, containerRef };
}
